// Shared page-container wrapper. Per spec/build-plan.md P-024 row + the
// D-084 / D-085 deferral chain.
//
// **Why extract now.** The `mx-auto max-w-screen-lg px-4` pattern was at
// 2 uses inside app.tsx (header inner-div + main inner-div) at P-022; the
// project's third-use threshold deferred extraction. P-024's read-only
// screens fan the pattern out: every list screen needs its own
// content-area wrapper, and the existing 2 sites in `<App />` continue.
// The shared component pins one place to evolve the layout (e.g. wider
// editor screens at P-031 / P-032) without sweeping every screen.
//
// **`as` polymorphic prop.** A boolean would only get us `<div>` vs one
// hardcoded alternative; an `as` prop lets the header keep its
// `<header>`-bound semantics by passing `as="header"` while staying
// thin. Defaults to `<div>` for screen content.
//
// **`y` prop for vertical padding.** Header chrome uses `py-3` (tighter,
// brand-band feel); main content uses `py-6` (room for the headings).
// A small string-enum keeps the contract narrow rather than threading a
// raw className through.
//
// **`size` prop for horizontal max-width (P-037.26 D-134).** Default
// `'normal'` = `max-w-screen-lg` (1024 px) — fits the list screens,
// home, library, gallery, and block-preview. `'wide'` = `max-w-screen-
// xl` (1280 px) — used by the Email + Template editor screens so the
// two-pane layout has room for a ≥680 px preview pane (matches
// `tokens.layout.containerWidth`) without shrinking the form pane below
// comfortable editing width. App.tsx picks the size by route.

import type { ComponentChildren } from 'preact'

import { mergeClass } from '../lib/merge-class'

interface PageContainerProps {
  readonly children: ComponentChildren
  readonly as?: 'div' | 'header' | 'main' | 'section' | 'nav'
  readonly y?: 'tight' | 'normal'
  readonly size?: 'normal' | 'wide'
  readonly class?: string
}

const Y_PADDING: Record<NonNullable<PageContainerProps['y']>, string> = {
  tight: 'py-3',
  normal: 'py-6',
}

const SIZE_MAX_WIDTH: Record<NonNullable<PageContainerProps['size']>, string> = {
  normal: 'max-w-screen-lg',
  wide: 'max-w-screen-xl',
}

export function PageContainer({
  children,
  as: Tag = 'div',
  y = 'normal',
  size = 'normal',
  class: extraClass,
}: PageContainerProps) {
  const baseClass = `mx-auto ${SIZE_MAX_WIDTH[size]} px-4 ${Y_PADDING[y]}`
  return <Tag class={mergeClass(baseClass, extraClass)}>{children}</Tag>
}
